import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { IUsersAdministrationUserModel } from "../../../usersAdministration/usersList/models/usersAdministrationUserModel";
import {
  IMassEmailFormModel,
  massEmailFormDefaultValues,
  massEmailSchema,
} from "../models/massEmailFormModel";
import { invalidateEmailLogsAfterDelay } from "./emailLogsUtility";

export const useMassEmailDialog = (onClose: () => void) => {
  const queryClient = useQueryClient();
  const [testEmailSent, setTestEmailSent] = useState(false);

  const form = useForm<IMassEmailFormModel>({
    resolver: yupResolver(massEmailSchema),
    defaultValues: massEmailFormDefaultValues,
  });

  const { data: users, isLoading: isUsersLoading } = useApiQuery<
    IUsersAdministrationUserModel[]
  >({
    queryKey: [endpointsMetadata.user.getAll],
    endpoint: endpointsMetadata.user.getAll,
    globalIsLoading: false,
  });

  const userOptions = useMemo(
    () =>
      (users ?? [])
        .filter((u) => !u.isDeleted)
        .map((u) => ({
          id: u.id,
          name: `${u.firstName} ${u.lastName} (${u.email})`,
        })),
    [users]
  );

  const { mutate: sendMassEmail, isLoading: isSending } = useApiMutation({
    onSuccess: () => {
      form.reset();
      onClose();
      invalidateEmailLogsAfterDelay(queryClient, 5000);
    },
  });

  const { mutate: sendTestEmail, isLoading: isSendingTest } = useApiMutation({
    onSuccess: () => {
      setTestEmailSent(true);
    },
  });

  const selectedUserIds = form.watch("userIds") ?? [];

  const handleSend = form.handleSubmit((data) => {
    const count = data.userIds?.length ?? 0;
    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да изпратите <b>${count}</b> ${count === 1 ? "имейл" : "имейла"}?`,
      () => {
        sendMassEmail({
          endpoint: endpointsMetadata.communicationLogs.sendMassEmail,
          method: "post",
          payload: data,
          successMessage: `Успешно изпратихте ${count} ${count === 1 ? "имейл" : "имейла"}.`,
          invalidateKeys: [[endpointsMetadata.communicationLogs.getEmailLogs]],
        });
      },
      undefined,
      "warning"
    );
  });

  const handleSendTestEmail = () => {
    const { subject, body, testEmail } = form.getValues();
    if (!testEmail) return;

    setTestEmailSent(false);
    sendTestEmail({
      endpoint: endpointsMetadata.communicationLogs.sendTestEmail,
      method: "post",
      payload: { subject, body, testEmail },
    });
  };

  return {
    form,
    userOptions,
    isUsersLoading,
    isSending,
    isSendingTest,
    testEmailSent,
    selectedUserIds,
    handleSend,
    handleSendTestEmail,
  };
};
